/**
 * SplitPane — chia khung theo CHIỀU DỌC (trên/dưới) với thanh ngăn KÉO ĐƯỢC.
 *
 * Dùng cho màn hình danh sách chứng từ: trên là bảng bản ghi, dưới là chi tiết dòng đang chọn.
 * Mặc định chia đôi; kéo thanh ngăn để đổi tỉ lệ, tỉ lệ được nhớ theo từng màn hình.
 */
import React, { useRef, useCallback, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import { ratioFromPointer, MIN_SPLIT_RATIO, MAX_SPLIT_RATIO } from './masterDetailSplit';

export default function SplitPane({ ratio, onRatioChange, onRatioCommit, top, bottom }) {
  const containerRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  // Giữ tỉ lệ mới nhất trong ref: hàm gỡ sự kiện chạy lúc thả chuột cần giá trị CUỐI CÙNG,
  // mà state thì chưa kịp cập nhật vào closure của listener.
  const latestRatioRef = useRef(ratio);
  latestRatioRef.current = ratio;

  const applyFromPointer = useCallback((clientY) => {
    const rect = containerRef.current && containerRef.current.getBoundingClientRect();
    if (!rect) return;
    const next = ratioFromPointer(clientY, rect);
    latestRatioRef.current = next;
    if (onRatioChange) onRatioChange(next);
  }, [onRatioChange]);

  useEffect(() => {
    if (!dragging) return undefined;

    const onMouseMove = (e) => { e.preventDefault(); applyFromPointer(e.clientY); };
    const onTouchMove = (e) => { if (e.touches && e.touches[0]) applyFromPointer(e.touches[0].clientY); };
    const stop = () => {
      setDragging(false);
      if (onRatioCommit) onRatioCommit(latestRatioRef.current);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', stop);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', stop);
    // Đang kéo thì chặn bôi đen chữ, nếu không con trỏ quét qua bảng là chọn cả vùng văn bản.
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', stop);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', stop);
      document.body.style.userSelect = prevUserSelect;
    };
  }, [dragging, applyFromPointer, onRatioCommit]);

  // Bàn phím: thanh ngăn nhận focus được, mũi tên lên/xuống đổi 2% mỗi lần.
  const onKeyDown = (e) => {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    e.preventDefault();
    const delta = e.key === 'ArrowUp' ? -0.02 : 0.02;
    const next = Math.min(MAX_SPLIT_RATIO, Math.max(MIN_SPLIT_RATIO, latestRatioRef.current + delta));
    latestRatioRef.current = next;
    if (onRatioChange) onRatioChange(next);
    if (onRatioCommit) onRatioCommit(next);
  };

  return (
    <Box ref={containerRef} sx={{ flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flex: `0 0 ${ratio * 100}%`, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {top}
      </Box>

      <Box
        role="separator"
        aria-orientation="horizontal"
        aria-label="Kéo để chia lại hai phần"
        tabIndex={0}
        onMouseDown={(e) => { e.preventDefault(); setDragging(true); }}
        onTouchStart={() => setDragging(true)}
        onKeyDown={onKeyDown}
        sx={{
          flex: '0 0 auto', height: 10, cursor: 'row-resize', display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: dragging ? 'primary.light' : '#eceff1',
          borderTop: '1px solid', borderBottom: '1px solid', borderColor: 'divider',
          '&:hover': { bgcolor: 'primary.light' },
          '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
        }}
      >
        <DragHandleIcon sx={{ fontSize: 14, color: dragging ? 'primary.contrastText' : 'text.disabled' }} />
      </Box>

      <Box sx={{ flex: '1 1 0', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {bottom}
      </Box>
    </Box>
  );
}
